import { Lock, Sparkles, ArrowRight, Zap } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { Card, CardContent, Button } from '@/components/ui';
import { useSubscription } from '@/contexts/SubscriptionContext';
import type { SubscriptionTier } from '@/types';
import type { FeatureName } from '@/types/subscription';

// Feature display names and descriptions
const FEATURE_INFO: Record<FeatureName, { name: string; description: string }> = {
  // Core
  basicReporting: { name: 'Basic Reporting', description: 'Submit and track maintenance reports' },
  workOrders: { name: 'Work Orders', description: 'Create and manage work orders' },
  vendorPortal: { name: 'Vendor Portal', description: 'Give vendors access to manage their work' },
  assetTracking: { name: 'Asset Tracking', description: 'Track and manage property assets' },
  // Analytics
  basicAnalytics: { name: 'Basic Analytics', description: 'View basic performance metrics' },
  advancedAnalytics: { name: 'Advanced Analytics', description: 'Deep insights and custom reports' },
  customReports: { name: 'Custom Reports', description: 'Build and export custom reports' },
  // IoT
  iotMonitoring: { name: 'IoT Monitoring', description: 'Monitor smart devices across properties' },
  smartLocks: { name: 'Smart Locks', description: 'Control and monitor smart locks remotely' },
  leakSensors: { name: 'Leak Sensors', description: 'Get alerts for water leaks instantly' },
  thermostats: { name: 'Smart Thermostats', description: 'Monitor and control HVAC remotely' },
  smokeDetectors: { name: 'Smoke Detectors', description: 'Real-time smoke and CO alerts' },
  motionSensors: { name: 'Motion Sensors', description: 'Track occupancy and motion events' },
  autoAlertReports: { name: 'Auto Alert Reports', description: 'Auto-create reports from device alerts' },
  // Disruptor Features
  qrCodeWorkflows: { name: 'QR Code Workflows', description: 'Generate and scan QR codes for assets and quick issue reporting' },
  magicLinkVendorPortal: { name: 'Magic Link Vendor Portal', description: 'Enable vendors to submit work without needing an account' },
  floorPlanMapping: { name: 'Floor Plan Mapping', description: 'Upload floor plans and place interactive asset markers' },
  // Integrations
  emailIntegration: { name: 'Email Integration', description: 'Send notifications via email' },
  smsNotifications: { name: 'SMS Notifications', description: 'Send text message alerts' },
  apiAccess: { name: 'API Access', description: 'Build custom integrations with our API' },
  webhooks: { name: 'Webhooks', description: 'Receive real-time event notifications' },
  customIntegrations: { name: 'Custom Integrations', description: 'Connect with any third-party service' },
  // Enterprise
  sso: { name: 'Single Sign-On', description: 'Enterprise SSO with SAML/OIDC' },
  auditLog: { name: 'Audit Log', description: 'Complete activity tracking and compliance' },
  whiteLabel: { name: 'White Label', description: 'Custom branding for your organization' },
  dedicatedSupport: { name: 'Dedicated Support', description: '24/7 priority support channel' },
  slaGuarantee: { name: 'SLA Guarantee', description: 'Guaranteed uptime and response times' },
};

const TIER_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

interface UpgradePromptProps {
  feature?: FeatureName;
  requiredTier?: SubscriptionTier;
  title?: string;
  description?: string;
  variant?: 'card' | 'inline' | 'banner' | 'modal';
  onUpgradeClick?: () => void;
}

export function UpgradePrompt({
  feature,
  requiredTier,
  title,
  description,
  variant = 'card',
  onUpgradeClick,
}: UpgradePromptProps) {
  const router = useRouter();
  const { getPlanByTier } = useSubscription();

  const featureInfo = feature ? FEATURE_INFO[feature] : null;
  const tierName = requiredTier ? TIER_NAMES[requiredTier] : 'a higher';
  const plan = requiredTier ? getPlanByTier(requiredTier) : null;

  const displayTitle = title || (featureInfo ? `Unlock ${featureInfo.name}` : 'Upgrade Required');
  const displayDescription =
    description ||
    (featureInfo
      ? `${featureInfo.description}. Upgrade to ${tierName} plan to access this feature.`
      : `This feature requires the ${tierName} plan or higher.`);

  const handleUpgrade = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      router.push('/billing');
    }
  };

  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary-500/10 to-primary-600/10 dark:from-primary-500/20 dark:to-primary-600/20 px-3 py-2 text-sm">
        <Lock className="h-4 w-4 text-primary-500" />
        <span className="text-secondary-700 dark:text-secondary-300">{displayTitle}</span>
        <button
          onClick={handleUpgrade}
          className="ml-auto font-medium text-primary-600 dark:text-primary-400 hover:underline"
        >
          Upgrade
        </button>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-3 text-white shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-white/20 p-2">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold">{displayTitle}</p>
              <p className="text-sm text-primary-100">{displayDescription}</p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleUpgrade}
            className="shrink-0 bg-white text-primary-700 hover:bg-primary-50"
          >
            Upgrade Now
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/10" />
        <div className="absolute -bottom-4 -right-4 h-16 w-16 rounded-full bg-white/5" />
      </div>
    );
  }

  // Default card variant
  return (
    <Card className="relative overflow-hidden border-dashed border-2 border-secondary-200 dark:border-secondary-700 bg-gradient-to-br from-secondary-50 to-secondary-100 dark:from-secondary-800/50 dark:to-secondary-900/50">
      <CardContent className="py-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-500/20">
          <Lock className="h-6 w-6 text-primary-600 dark:text-primary-400" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-secondary-900 dark:text-secondary-100">
          {displayTitle}
        </h3>
        <p className="mb-6 text-sm text-secondary-600 dark:text-secondary-400 max-w-sm mx-auto">
          {displayDescription}
        </p>
        {plan && (
          <p className="mb-4 text-sm text-secondary-500 dark:text-secondary-500">
            Starting at <span className="font-semibold text-primary-600 dark:text-primary-400">${plan.pricing.monthly}/mo</span>
          </p>
        )}
        <Button variant="primary" onClick={handleUpgrade}>
          <Zap className="mr-2 h-4 w-4" />
          Upgrade to {tierName}
        </Button>
      </CardContent>
      {/* Decorative elements */}
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-primary-500/10 dark:bg-primary-500/5" />
      <div className="absolute -left-4 -bottom-4 h-16 w-16 rounded-full bg-primary-500/5 dark:bg-primary-500/5" />
    </Card>
  );
}

// Simple badge for locked items in lists
export function LockedBadge({ tier }: { tier?: SubscriptionTier }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 dark:bg-secondary-800 px-2 py-0.5 text-xs font-medium text-secondary-600 dark:text-secondary-400">
      <Lock className="h-3 w-3" />
      {tier ? TIER_NAMES[tier] : 'Pro'}
    </span>
  );
}
